import { useEffect, useState } from "react";
import type { DependencyGraph } from "../../models";

interface State {
  data: DependencyGraph | null;
  loading: boolean;
  error: string | null;
}

/**
 * Loader hook for any DependencyGraph-returning fetcher.
 * Pass `null` to disable. Re-runs whenever the fetcher reference changes,
 * so callers should memoize the fetcher with useMemo/useCallback.
 */
export function useDependencyGraph(
  fetcher: (() => Promise<DependencyGraph>) | null,
): State {
  const [state, setState] = useState<State>({ data: null, loading: false, error: null });

  useEffect(() => {
    if (!fetcher) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetcher()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed to load dependency graph";
        setState({ data: null, loading: false, error: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher]);

  return state;
}
